import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.14 },
  }),
};

const Line = ({ children, i }) => (
  <div className="overflow-hidden">
    <motion.div custom={i} variants={line} initial="hidden" animate="visible">
      {children}
    </motion.div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-cream">
      {/* Parallax hero image */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.hero} alt="A pristine, freshly cleaned kitchen by Klean With Katie" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-espresso/55 via-espresso/10 to-transparent" />
        </div>
      </motion.div>

      <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 md:px-12 pb-16 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="text-xs md:text-sm uppercase tracking-[0.3em] text-cream/90 mb-6"
        >
          Making Homes Sparkle · Deep Clean Specialist
        </motion.p>

        <h1 className="font-serif font-light text-cream leading-[0.92] tracking-tight text-5xl md:text-7xl lg:text-8xl">
          <Line i={0}>Immaculate spaces.</Line>
          <Line i={1}><span className="italic text-blush-light">Intentional</span> living.</Line>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-end gap-8 sm:gap-16"
        >
          <p className="max-w-md text-base md:text-lg font-light text-cream/90 leading-relaxed">
            A meticulous, human touch for homes that deserve more than a quick wipe-down. Cared for, corner to corner.
          </p>
          <button
            data-testid="hero-quote-button"
            onClick={() => scrollToId("contact")}
            className="group shrink-0 inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] px-8 py-4 bg-blush text-white hover:bg-cream hover:text-espresso transition-colors duration-300 rounded-full"
          >
            Book Your Clean
            <ArrowDown size={16} className="group-hover:translate-y-1 transition-transform duration-300" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};
